/* eslint-disable @typescript-eslint/no-explicit-any */
import { ProductItem } from '@/types/product';
import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const generateSlug = (str: string): string => {
  let slug = str.toLowerCase();

  slug = slug
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-') // Replace spaces with '-'
    .replace(/--+/g, '-'); // Replace multiple '-' with a single '-'

  const specialCases: { [key: string]: string } = {
    '&': 'and',
  };

  for (const char in specialCases) {
    slug = slug.replace(new RegExp(char, 'g'), specialCases[char]);
  }

  slug = slug.replace(/^-+|-+$/g, '');

  return slug;
};

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const isEmpty = (obj: any) =>
  [Object, Array].includes((obj || {}).constructor) && !Object.entries(obj || {}).length;

export const formatIDR = (value: string | number) =>
  `Rp ${(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}`;

export const formatNumber = (value: string | number) =>
  `${(value || 0).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')}`;

export function debounce<T extends (...args: any[]) => void>(
  callback: T,
  ms: number
): (...args: Parameters<T>) => void {
  let timer: NodeJS.Timeout | undefined;

  return (...args: Parameters<T>): void => {
    if (timer) {
      clearTimeout(timer);
    }

    timer = setTimeout(() => {
      callback(...args);
    }, ms);
  };
}

export function subdomainSuggestion(storeName: string, maxLength: number = 16): string[] {
  // Function to clean up the store name
  const cleanName = (name: string): string => {
    return name.replace(/[^a-zA-Z0-9\s]/g, '').toLowerCase();
  };

  // Function to generate permutations of an array
  const permute = (arr: string[]): string[][] => {
    if (arr.length === 0) return [[]];
    return arr.flatMap((item, i) =>
      permute([...arr.slice(0, i), ...arr.slice(i + 1)]).map((p) => [item, ...p])
    );
  };

  const cleanedName = cleanName(storeName);
  const words = cleanedName.split(/\s+/);

  // Generate permutations of the words for variety
  const permutedWords = permute(words);

  // Create subdomain suggestions

  const subdomainSuggestions = permutedWords.map((p) => {
    const joined = p.join('-');
    return joined.length > maxLength ? joined.slice(0, maxLength) : joined;
  });

  // Limit to unique suggestions and a maximum of 10
  const uniqueSuggestions = Array.from(new Set(subdomainSuggestions)).slice(0, 4);

  return uniqueSuggestions;
}

export const initialGroupFormated = (groupVariants: { label: string; values: string[] }[] = []) => {
  if (!groupVariants) {
    return [];
  }
  const formattedVariants = groupVariants.map((group) => ({
    label: group.label,
    value: group.label,
    variants: group.values.map((value) => ({
      label: value,
      value: value,
      __isNew__: true,
    })),
  }));

  return formattedVariants;
};

export const validStringIsUrl = (str: string) => {
  try {
    new URL(str);
    return true;
  } catch (error) {
    return false;
  }
};

export const formatImageProductItems = (itemVariants: ProductItem[]) => {
  itemVariants.map((item) => {
    if (item.images && item.images.length > 0) {
      const images = item.images.map((image: { assets_url: any }) => image.assets_url);
      item.images = images;
    }
  });

  return itemVariants;
};

export const extractNumber = (value: string): number => {
  const numberOnly = value.replace(/\D/g, '');
  const parsedNumber = parseInt(numberOnly, 10);
  return isNaN(parsedNumber) ? 0 : parsedNumber;
};

export const extractPercent = (value: string): number => {
  const numberOnly = value.replace(/\D/g, '');
  const parsedNumber = parseInt(numberOnly, 10);
  return isNaN(parsedNumber) ? 0 : parsedNumber > 100 ? 100 : parsedNumber;
};

export const formatCurrency = (value: number, isDecimal: boolean = false) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'decimal',
    currency: 'IDR',
    minimumFractionDigits: isDecimal ? 2 : 0,
    maximumFractionDigits: isDecimal ? 2 : 0,
  }).format(value);
};

export function bytesToSize(bytes: number): string {
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
  if (bytes === 0) return 'n/a';
  const i = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), sizes.length - 1);
  if (i === 0) return `${bytes} ${sizes[i]}`;
  return `${(bytes / 1024 ** i).toFixed(1)} ${sizes[i]}`;
}

export function getInnerText(html: string): string {
  const parser = new DOMParser();
  const doc = parser.parseFromString((html || '') as string, 'text/html');
  const text = doc.body.textContent || doc.body.innerText;
  return text;
}

export const removeHTMLTags = (str: string) => str?.replace(/<[^>]*>|&nbsp;|&[a-z]+;|\n/g, '');

export const truncateText = (str: string, limit: number) => {
  if (str?.length > limit) {
    return str.slice(0, limit) + '...';
  }
  return str;
};

export function hexToRgb(hex: string, percent = 100) {
  hex = hex.replace(/^#/, '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  return `rgb(${r}, ${g}, ${b}, ${percent / 100})`;
}

export const formatEstimateDelivery = (etaFrom: string, etaTo: string) => {
  const startDate = new Date(etaFrom);
  const endDate = new Date(etaTo);
  let days = 0;

  for (let date = new Date(startDate); date <= endDate; date.setDate(date.getDate() + 1)) {
    const day = date.getDay();
    if (day !== 0 && day !== 6) {
      days++;
    }
  }

  if (days === 0) {
    return 'Today';
  } else if (days === 1) {
    return `Tomorrow`;
  } else {
    return `${days} days`;
  }
};

export const generateUniqueId = () => {
  const timestamp = Date.now();
  const randomPart = Math.random().toString(36).substring(2, 9);
  return `${timestamp}-${randomPart}`;
};

export const formatOrderStatus = (status: string) => {
  switch (status) {
    case 'ready_to_process':
      return 'Ready to process';
    case 'processing':
      return 'Processing';
    case 'shipment':
      return 'Shipment';
    case 'done':
      return 'Done';
    case 'cancel':
      return 'Cancel';
    case 'not_yet_shipped':
      return 'Not yet shipped';
    case 'shipped':
      return 'Shipped';
    case 'expired':
      return 'Expired';
    default:
      return 'Unpaid';
  }
};

export const fileExtension = (fileName: string) => {
  const ext = fileName.split('.').pop();
  if (ext) {
    return ext.toLowerCase();
  }
  return '';
};

export const formatOperatorCollection = (operator: string) => {
  switch (operator) {
    case 'is_equal_to':
      return 'is equal to';
    case 'is_not_equal_to':
      return 'is not equal to';
    case 'starts_with':
      return 'starts with';
    case 'ends_with':
      return 'ends with';
    case 'contains':
      return 'contains';
    case 'does_not_contain':
      return 'does not contain';
    case 'is_greater_than':
      return 'is greater than';
    case 'is_less_than':
      return 'is less than';
    default:
      return 'is equal to';
  }
};

export const extractYouTubeVideoId = (url: string): string | null => {
  const shortRegex =
    /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/(?:shorts\/|watch\?v=)|youtu\.be\/)([^&?]{11})/;
  const longRegex = /(?:https?:\/\/)?(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?]{11})/;

  const shortMatch = url?.match(shortRegex);
  const longMatch = url?.match(longRegex);

  return shortMatch ? shortMatch[1] : longMatch ? longMatch[1] : null;
};
